// Rules (docs/app.md → Rules). Default rules for invoice routing: which
// vendor coding rule applies (Accounts) and which company a sender's mail
// routes to (Companies). Split out of References (owner item 10a,
// 2026-08-13) — a rule decides WHERE an invoice goes, not just what a master
// record says.
//
// Gated on the new `rules` role, the way References is gated on Training —
// see `App.tsx`. TRANSITION: the underlying endpoints accept `rules` OR
// `references` (`canManageRules` in `backend/src/domain/authority.ts`), so an
// existing References holder keeps write access at cutover, but the SCREEN
// itself is reached only through the new role (References keeps its own
// Vendors/Companies/Accounts/Vehicles tabs and no longer shows Defaults or
// Senders).
//
// Three sub-tabs: Accounts (the vendor coding rules manager, formerly
// References → Defaults), Companies (the sender/company routing manager,
// formerly References → Senders), moved verbatim, and Vendors (owner item
// 10b, 2026-08-13) — a rule reading "when an invoice arrives from
// <alias_name>, attribute it to <vendor>", backed by `vendor_aliases`
// (extended with a writer, not a new table — `docs/decisions.md`). A match
// is a DETERMINISTIC OVERRIDE of vendor identity, ahead of vendor precedent
// (owner item 10c; `docs/ai.md` §3.2) — stronger than the vendor coding
// rules on the Accounts tab, which are a soft preference pass 2 can still
// weigh against the document.
//
// The small list-row primitives below (MasterRow, ConfirmDialog, useToast,
// writeError) are copies of the ones TrainingScreen.tsx keeps for its own
// remaining tabs — duplicated rather than extracted into a shared module, so
// this screen's move is a plain cut-and-paste with no new shared surface for
// two screens to both depend on and drift out from under.
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  SearchBox,
  Spinner,
  Switch,
  Tab,
  TabList,
  Toast,
  ToastTitle,
  makeStyles,
  mergeClasses,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import {
  AddRegular,
  ArchiveRegular,
  ArrowUndoRegular,
  DismissRegular,
  EditRegular,
} from '@fluentui/react-icons'
import { errorMessage, isApiError } from '../api/errors'
import type { VendorAlias, VendorCodingRule } from '../lib/types'
import { SCREEN_SUBHEADER, TOASTER_ID } from '../lib/constants'
import {
  createCompanyEmailDomain,
  createIntakeSender,
  createVendorAlias,
  createVendorCodingRule,
  loadCompaniesForManagement,
  loadCompanyEmailDomains,
  loadCostCenterLabels,
  loadCostCenterReach,
  loadExpenseTypeLabels,
  loadIntakeSenders,
  loadVendorAliases,
  loadVendorCodingRules,
  searchCostCenters,
  searchExpenseTypes,
  searchVendors,
  updateCompanyEmailDomain,
  updateIntakeSender,
  updateVendorAlias,
  updateVendorCodingRule,
} from '../lib/data'
import { entryIsActive, mergeSenderEntries, routedCompanyName, type SenderEntry } from '../lib/senders'
import { ScreenTitle } from '../components/ScreenTitle'
import { ErrorBanner } from '../components/States'
import { EntityPicker, type EntityOption } from '../components/EntityPicker'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  headerSub: { color: tokens.colorNeutralForeground3 },
  dialogField: { marginBottom: '14px' },
  pickerRow: { display: 'flex', gap: '8px', alignItems: 'flex-end' },
  senderBadges: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  // Inline, non-blocking dialog warning (mirrors States.tsx warningBanner and
  // TrainingScreen.tsx's identical class — see this file's header comment on
  // why the two are separate copies rather than a shared import).
  dialogWarn: {
    display: 'block',
    color: tokens.colorPaletteYellowForeground2,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 12px',
    marginBottom: '14px',
  },
  subnav: { marginBottom: '16px' },
  mdIntro: { display: 'block', color: tokens.colorNeutralForeground3, marginBottom: '12px' },
  mdToolbar: { display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' },
  searchBox: { minWidth: '280px' },
  spacer: { flex: 1 },
  mdHint: { color: tokens.colorNeutralForeground3, padding: '8px 2px' },
  mdList: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    maxHeight: '52vh',
    overflowY: 'auto',
  },
  mdRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  mdRowMain: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
  mdRowTitle: { fontWeight: tokens.fontWeightSemibold, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  // Opt-in only (vendor coding rules): the title there is a full sentence, not
  // a short name, and truncating it hides the expense type / cost center it
  // exists to show.
  mdRowTitleWrap: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', wordBreak: 'break-word' },
  mdRowSub: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  mdRowActions: { display: 'flex', gap: '4px', alignItems: 'center' },
  mdRetired: { opacity: 0.55 },
})

/** A uniqueness violation is the database's answer, and the API's message names
    what collided — show it as written. Anything else gets the context of what we
    were trying to do. */
function writeError(e: unknown, context: string): string {
  if (isApiError(e) && e.code === 'duplicate_key') return e.message
  return `${context}: ${errorMessage(e)}`
}

/** Toast helper shared by the managers. */
function useToast() {
  const { dispatchToast } = useToastController(TOASTER_ID)
  return useCallback(
    (title: string, intent: 'success' | 'error' = 'success') =>
      dispatchToast(
        <Toast>
          <ToastTitle>{title}</ToastTitle>
        </Toast>,
        { intent },
      ),
    [dispatchToast],
  )
}

/** One row in a master list: key fields + Active/Retired badge + actions. */
function MasterRow({
  title,
  subtitle,
  retired,
  busy,
  onEdit,
  onToggleActive,
  wrapTitle,
}: {
  title: string
  subtitle?: string
  retired: boolean
  busy: boolean
  onEdit?: () => void
  onToggleActive: () => void
  /** Lets the title wrap instead of ellipsizing — set only where the title is a
      full sentence rather than a short name (vendor coding rules). */
  wrapTitle?: boolean
}) {
  const s = useStyles()
  return (
    <div className={mergeClasses(s.mdRow, retired && s.mdRetired)}>
      <div className={s.mdRowMain}>
        {/* The native tooltip is for the ellipsized case — where the row hides
            part of its own title. A wrapping title shows all of itself, so a
            tooltip repeating it would just cover the row underneath. */}
        <span
          className={mergeClasses(s.mdRowTitle, wrapTitle && s.mdRowTitleWrap)}
          title={wrapTitle ? undefined : title}
        >
          {title}
        </span>
        {subtitle && <span className={s.mdRowSub}>{subtitle}</span>}
      </div>
      <Badge appearance="outline" color={retired ? 'danger' : 'success'}>
        {retired ? 'Retired' : 'Active'}
      </Badge>
      <div className={s.mdRowActions}>
        {onEdit && (
          <Button
            appearance="subtle"
            size="small"
            icon={<EditRegular />}
            aria-label="Edit"
            disabled={busy}
            onClick={onEdit}
          />
        )}
        <Button
          appearance="subtle"
          size="small"
          icon={retired ? <ArrowUndoRegular /> : <ArchiveRegular />}
          disabled={busy}
          onClick={onToggleActive}
        >
          {retired ? 'Reactivate' : 'Retire'}
        </Button>
        {busy && <Spinner size="tiny" />}
      </div>
    </div>
  )
}

/** Generic confirm dialog for retire/reactivate. */
function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  body: string
  confirmLabel: string
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onCancel()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <Body1>{body}</Body1>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={onConfirm} disabled={busy}>
              {confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

type RuleTab = 'accounts' | 'companies' | 'vendors'

export function RulesScreen() {
  const s = useStyles()
  const [tab, setTab] = useState<RuleTab>('accounts')
  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>Rules</ScreenTitle>
        <Body1 className={s.headerSub}>{SCREEN_SUBHEADER.Rules}</Body1>
      </div>
      <TabList
        appearance="subtle"
        size="small"
        className={s.subnav}
        selectedValue={tab}
        onTabSelect={(_, d) => setTab(d.value as RuleTab)}
        aria-label="Rules tables"
      >
        <Tab value="accounts">Accounts</Tab>
        <Tab value="companies">Companies</Tab>
        <Tab value="vendors">Vendors</Tab>
      </TabList>
      {tab === 'accounts' && <VendorDefaultsManager />}
      {tab === 'companies' && <SendersManager />}
      {tab === 'vendors' && <VendorAliasesManager />}
    </div>
  )
}

// ---- Accounts (vendor coding rules, formerly References → Defaults) --------
//
// A rule says either half of an account, or both, optionally narrowed to lines
// whose description contains a phrase. Each one reads back as a full sentence,
// with no second line and no source annotation — staff are the only ones who
// ever add one, so naming the source on screen tells nobody anything:
//
//   When an invoice from GORDON FOOD SERVICE has any line, default the expense
//   type to Food & Staples and the cost center to Dietary.
//
// A blank scope reads "has any line" in its place, and a rule naming only one
// half drops the other clause. This fires per LINE ITEM during the pass-2
// matching run (backend/src/domain/matchingLadder.ts, backend/src/lambda/
// match.ts) — not "when an invoice is received", which is a different, false
// claim about the same rule.

/** The scope normalization, mirrored from `normalizeDescription` for preview. */
function normalizeScope(value: string): string {
  return value
    .toLowerCase()
    .replace(/[.,\-/&#]/g, ' ')
    .replace(/[0-9]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function VendorDefaultsManager() {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<VendorCodingRule[] | null>(null)
  const [showRetired, setShowRetired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [edit, setEdit] = useState<VendorCodingRule | 'new' | null>(null)
  const [confirm, setConfirm] = useState<VendorCodingRule | null>(null)
  const [term, setTerm] = useState('')
  const [expenseTypeNames, setExpenseTypeNames] = useState<ReadonlyMap<string, string>>(new Map())
  const [costCenterNames, setCostCenterNames] = useState<ReadonlyMap<string, string>>(new Map())

  const load = useCallback(async () => {
    setError(null)
    try {
      // The two halves come from the LABEL loaders, not the pickers: they are
      // memoized, they ask for the whole list, and they carry no `active`
      // filter. A rule may name a half retired under it — that rule still has
      // to read back as a sentence rather than as digits.
      const [rules, expenseTypes, costCenters] = await Promise.all([
        loadVendorCodingRules(),
        loadExpenseTypeLabels().catch(() => new Map<string, string>()),
        loadCostCenterLabels().catch(() => new Map<string, string>()),
      ])
      setAll(rules)
      setExpenseTypeNames(expenseTypes)
      setCostCenterNames(costCenters)
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  // The vendor name rides the row itself now — `GET /vendor-coding-rules`
  // resolves it server-side with a read-time join onto `vendors`
  // (`docs/api.md` §15.13–15.15) — so there is no id-to-name lookup to run
  // here any more. `vendor_id` is the last-resort fallback, for the
  // unreachable case the join itself already treats as its own fallback.
  const vendorName = useCallback((r: VendorCodingRule) => r.vendor_name || r.vendor_id, [])
  const expenseTypeName = useCallback(
    (code?: string | null) => (code ? expenseTypeNames.get(code) ?? code : ''),
    [expenseTypeNames],
  )
  const costCenterName = useCallback(
    (code?: string | null) => (code ? costCenterNames.get(code) ?? code : ''),
    [costCenterNames],
  )

  /** The rule as a full, true sentence — the whole point of the row. It fires
      per LINE ITEM during pass-2 matching, so the lead clause says "has a
      line", never "is received". There is no Account clause: a rule names
      either half, or both — never a whole account — and the CHECK constraint
      `vendor_coding_rule_says_something` bars neither being set, but the final
      fallback below covers that case anyway rather than emitting a dangling
      "default the". */
  const sentence = useCallback(
    (r: VendorCodingRule): string => {
      const who = vendorName(r)
      const scope = r.description_scope?.trim()
      const lead = scope
        ? `When an invoice from ${who} has a line mentioning "${scope}"`
        : `When an invoice from ${who} has any line`
      const et = r.expense_type_code ? expenseTypeName(r.expense_type_code) : null
      const cc = r.cost_center_code ? costCenterName(r.cost_center_code) : null
      if (et && cc) return `${lead}, default the expense type to ${et} and the cost center to ${cc}.`
      if (et) return `${lead}, default the expense type to ${et}.`
      if (cc) return `${lead}, default the cost center to ${cc}.`
      return `${lead}.`
    },
    [vendorName, expenseTypeName, costCenterName],
  )

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    // Search the STRUCTURED fields, not the sentence: the sentence's own
    // template words ("expense type", "cost center", "invoice", "line",
    // "default", "any") would otherwise match every row.
    const haystack = (d: VendorCodingRule) =>
      [
        d.vendor_id,
        vendorName(d),
        d.description_scope ?? '',
        d.expense_type_code ?? '',
        d.cost_center_code ?? '',
        expenseTypeName(d.expense_type_code),
        costCenterName(d.cost_center_code),
      ]
        .join(' ')
        .toLowerCase()
    return (all ?? [])
      .filter((d) => showRetired || d.is_active)
      .filter((d) => !q || haystack(d).includes(q))
  }, [all, showRetired, term, vendorName, expenseTypeName, costCenterName])

  async function toggleActive(d: VendorCodingRule) {
    setBusyId(d.id)
    setError(null)
    try {
      await updateVendorCodingRule(d.id, { is_active: !d.is_active, row_version: d.row_version })
      toast(d.is_active ? 'Default retired' : 'Default reactivated')
      setConfirm(null)
      await load()
    } catch (e) {
      setError(writeError(e, 'Could not update default'))
    } finally {
      setBusyId(null)
    }
  }

  if (all === null) return <Spinner label="Loading defaults…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <div className={s.mdToolbar}>
        <Switch label="Show retired" checked={showRetired} onChange={(_, d) => setShowRetired(d.checked)} />
        <SearchBox
          className={s.searchBox}
          placeholder="Search vendor, expense type or cost center…"
          value={term}
          onChange={(_, d) => setTerm(d.value)}
        />
        <div className={s.spacer} />
        <Button icon={<AddRegular />} onClick={() => setEdit('new')}>
          Add default
        </Button>
      </div>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>{(all ?? []).length === 0 ? 'No defaults to show.' : 'No defaults match.'}</Body1>
      ) : (
        <div className={s.mdList}>
          {visible.map((d) => (
            <MasterRow
              key={d.id}
              title={sentence(d)}
              wrapTitle
              retired={!d.is_active}
              busy={busyId === d.id}
              onEdit={() => setEdit(d)}
              onToggleActive={() => (d.is_active ? setConfirm(d) : void toggleActive(d))}
            />
          ))}
        </div>
      )}
      <VendorDefaultDialog
        row={edit}
        vendorName={vendorName}
        expenseTypeName={expenseTypeName}
        costCenterName={costCenterName}
        onClose={() => setEdit(null)}
        onSaved={(wasNew) => {
          setEdit(null)
          toast(wasNew ? 'Default added' : 'Default updated')
          void load()
        }}
        onError={setError}
      />
      <ConfirmDialog
        open={!!confirm}
        title="Retire default?"
        body={confirm ? `Retire this default? ${sentence(confirm)} You can reactivate it later.` : ''}
        confirmLabel="Retire"
        busy={!!busyId}
        onConfirm={() => confirm && void toggleActive(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  )
}

function VendorDefaultDialog({
  row,
  vendorName,
  expenseTypeName,
  costCenterName,
  onClose,
  onSaved,
  onError,
}: {
  row: VendorCodingRule | 'new' | null
  /** Resolvers from `VendorDefaultsManager`'s label loaders — used only to seed
      an EDIT with labels instead of raw codes; a fresh pick already carries its
      own label via `EntityPicker`'s `onSelect`. */
  vendorName: (r: VendorCodingRule) => string
  expenseTypeName: (code?: string | null) => string
  costCenterName: (code?: string | null) => string
  onClose: () => void
  onSaved: (wasNew: boolean) => void
  onError: (m: string) => void
}) {
  const s = useStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [vendor, setVendor] = useState<{ id?: string; name?: string }>({})
  const [expenseType, setExpenseType] = useState<{ id?: string; name?: string }>({})
  const [costCenter, setCostCenter] = useState<{ id?: string; name?: string }>({})
  const [scope, setScope] = useState('')
  const [saving, setSaving] = useState(false)
  /** How constrained a cost-center-only rule is: it applies only where it composes. */
  const [costCenterReach, setCostCenterReach] = useState<{ fits: number; total: number } | null>(null)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setVendor({})
      setExpenseType({})
      setCostCenter({})
      setScope('')
      return
    }
    // Seed with LABELS, not codes — an edit used to open showing "67-502"
    // rather than the account's actual name.
    setVendor({ id: row.vendor_id, name: vendorName(row) })
    setExpenseType({
      id: row.expense_type_code ?? undefined,
      name: row.expense_type_code ? expenseTypeName(row.expense_type_code) : undefined,
    })
    setCostCenter({
      id: row.cost_center_code ?? undefined,
      name: row.cost_center_code ? costCenterName(row.cost_center_code) : undefined,
    })
    setScope(row.description_scope ?? '')
    // `row` is the only thing this effect seeds from — the label resolvers are
    // memoized callbacks from the manager and stable across a render, and
    // listing them here would not change when they fire, only add churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row])

  // The reach warning is only meaningful on a cost-center-ONLY rule: with an
  // expense type set the pair is fixed and the server refuses a non-composing
  // one. Computed over the WHOLE chart (`loadCostCenterReach`), not one page —
  // see that function's docstring for the defect a page-bounded count caused.
  useEffect(() => {
    if (!costCenter.id || expenseType.id) {
      setCostCenterReach(null)
      return
    }
    let cancelled = false
    loadCostCenterReach(costCenter.id)
      .then((reach) => {
        if (!cancelled) setCostCenterReach(reach)
      })
      .catch(() => !cancelled && setCostCenterReach(null))
    return () => {
      cancelled = true
    }
  }, [costCenter.id, expenseType.id])

  const normalizedScope = normalizeScope(scope)
  const scopeChanged = scope.trim() !== '' && normalizedScope !== scope.trim().toLowerCase()
  const scopeEmptied = scope.trim() !== '' && normalizedScope === ''
  // A rule must say something. Both halves set is validated by the SERVER, which
  // refuses a pair that is not a real account — a pre-flight check the server
  // then re-checks is a race, and this screen does not keep the chart.
  const ready = !!vendor.id && (!!expenseType.id || !!costCenter.id)

  async function save() {
    if (!ready || !vendor.id) return
    setSaving(true)
    try {
      const body = {
        description_scope: normalizedScope === '' ? null : normalizedScope,
        expense_type_code: expenseType.id ?? null,
        cost_center_code: costCenter.id ?? null,
      }
      if (isNew) {
        await createVendorCodingRule({ vendor_id: vendor.id, ...body })
      } else if (existing) {
        await updateVendorCodingRule(existing.id, { ...body, row_version: existing.row_version })
      }
      onSaved(isNew)
    } catch (e) {
      onError(writeError(e, 'Could not save default'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{isNew ? 'Add default' : 'Edit default'}</DialogTitle>
          <DialogContent>
            <Field label="Vendor" className={s.dialogField}>
              {isNew ? (
                <EntityPicker
                  value={vendor}
                  placeholder="Search vendors…"
                  search={(t) =>
                    searchVendors(t).then<EntityOption[]>((vs) =>
                      vs.map((v) => ({
                        id: v.vendor_id,
                        primary: v.vendor_name || '(unnamed)',
                        secondary: [v.city, v.state_or_province].filter(Boolean).join(', ') || undefined,
                      })),
                    )
                  }
                  onSelect={(o) => setVendor({ id: o.id, name: o.primary })}
                />
              ) : (
                <Input value={existing?.vendor_id ?? ''} readOnly appearance="filled-darker" />
              )}
            </Field>
            <Field
              label="Only lines containing"
              hint="Leave blank to apply to every line from this vendor."
              className={s.dialogField}
            >
              <Input value={scope} onChange={(_, d) => setScope(d.value)} placeholder="e.g. shirt" />
            </Field>
            {scopeEmptied ? (
              <Body1 className={s.dialogWarn}>
                That text normalizes to nothing, so the rule would apply to every line from this
                vendor. Line descriptions are matched with digits removed and punctuation collapsed.
              </Body1>
            ) : scopeChanged ? (
              <Body1 className={s.dialogWarn}>
                Matched as &ldquo;{normalizedScope}&rdquo; — line descriptions are compared with
                digits removed and punctuation collapsed.
              </Body1>
            ) : null}
            <Field label="What is it? (Expense Type)" className={s.dialogField}>
              <div className={s.pickerRow}>
                <EntityPicker
                  value={expenseType}
                  placeholder="Search Expense Types…"
                  search={(t) =>
                    searchExpenseTypes(t).then<EntityOption[]>((es) =>
                      es.map((e) => ({ id: e.expense_type_code, primary: e.label, secondary: e.expense_type_code })),
                    )
                  }
                  onSelect={(o) => {
                    setExpenseType({ id: o.id, name: o.primary })
                    // Changing the Expense Type invalidates whatever Cost Center
                    // was chosen against the OLD one — the same reset the invoice
                    // modals apply (owner ruling 2026-08-10). This runs only from
                    // an actual pick, never from an effect keyed on
                    // `expenseType.id`: an effect would also fire when the
                    // seeding effect above sets both halves for an EXISTING
                    // default, wiping the saved Cost Center of every row merely
                    // opened for edit.
                    setCostCenter({})
                  }}
                />
                {expenseType.id && (
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Clear Expense Type"
                    onClick={() => {
                      setExpenseType({})
                      setCostCenter({})
                    }}
                  />
                )}
              </div>
            </Field>
            <Field label="Who is it for? (Cost Center)" className={s.dialogField}>
              <div className={s.pickerRow}>
                <EntityPicker
                  value={costCenter}
                  placeholder="Search Cost Centers…"
                  optionsKey={expenseType.id ?? ''}
                  search={(t) =>
                    searchCostCenters(t, expenseType.id).then<EntityOption[]>((cs) =>
                      cs.map((c) => ({ id: c.cost_center_code, primary: c.label, secondary: c.cost_center_code })),
                    )
                  }
                  onSelect={(o) => setCostCenter({ id: o.id, name: o.primary })}
                />
                {costCenter.id && (
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Clear Cost Center"
                    onClick={() => setCostCenter({})}
                  />
                )}
              </div>
            </Field>
            {costCenterReach !== null && (
              <Body1 className={s.dialogWarn}>
                A rule with only a Cost Center applies only where it composes a real account with
                the Expense Type that resolves. {costCenter.name ?? costCenter.id} composes with{' '}
                {costCenterReach.fits} of the {costCenterReach.total} Expense Types; on every other
                line the rule contributes nothing.
              </Body1>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              {isNew ? 'Add default' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

// ---- Companies (sender/company routing, formerly References → Senders) -----
// This tab shows two facts side by side, per entry: `intake_senders` (may this
// address email documents in) and `company_email_domains` (which company a
// domain's uploads and emails file to). They stay two tables and two writes —
// `mergeSenderEntries` (`../lib/senders`) only joins them for display, on the
// exact stored string. The uploader-domain rung (`company_email_domains`)
// governs IN-APP uploads as well as email — `uploads.ts` sets `source` from
// the signed-in principal for a non-service caller, `extract.ts` copies it to
// `invoices.uploader_email`, and `matchingLadder.ts` matches that address's
// domain against this same table. Editing a company's domains from
// References → Companies still works; this is the second door onto the same
// two masters.

/** Trim + lowercase, and prepend a leading `@` when the typed text has no `@`
    at all — so a bare domain like `pas-lp.com` is stored as `@pas-lp.com`,
    the same convention `normalizeDomain` uses for the Companies tab's domain
    editor. An address the caller already typed with an `@` is left exactly as
    typed (just trimmed and lowercased) — never widened into a domain. */
function normalizeSenderKey(raw: string): string {
  const t = raw.trim().toLowerCase()
  if (t === '' || t.includes('@')) return t
  return `@${t}`
}

/** One row: the key, both facts as badges, and a two-line subtitle spelling
    out what each fact means for THIS entry — not `MasterRow`, which carries
    one Active/Retired badge and one Retire button and cannot express two
    independent lifecycles. */
function SenderRow({
  entry,
  companyNames,
  onEdit,
}: {
  entry: SenderEntry
  companyNames: ReadonlyMap<string, string>
  onEdit: () => void
}) {
  const s = useStyles()
  const intakeClause = entry.intake
    ? entry.isDomain
      ? 'Any sender at this domain may email documents into intake'
      : 'May email documents into intake'
    : entry.intakeHistory.length > 0
      ? 'Mail from this entry is skipped'
      : 'Not on the intake list'
  const resolvedName = routedCompanyName(entry, companyNames)
  const routingClause = !entry.isDomain
    ? null
    : entry.routing
      ? `Uploads and emails from this domain file to ${resolvedName ?? entry.routing.company_id}`
      : 'No company routing — the document decides'
  const subtitle = [intakeClause, routingClause].filter((c): c is string => !!c).join('  ·  ')
  return (
    <div className={mergeClasses(s.mdRow, !entryIsActive(entry) && s.mdRetired)}>
      <div className={s.mdRowMain}>
        <span className={s.mdRowTitle} title={entry.key}>
          {entry.key}
        </span>
        <span className={s.mdRowSub}>{subtitle}</span>
      </div>
      <div className={s.senderBadges}>
        <Badge appearance="outline" color={entry.intake ? 'success' : 'subtle'}>
          {entry.intake ? 'Intake allowed' : 'Intake not allowed'}
        </Badge>
        {entry.isDomain && (
          <Badge appearance="outline" color={entry.routing ? 'brand' : 'subtle'}>
            {entry.routing ? `Routes to ${resolvedName ?? entry.routing.company_id}` : 'No routing'}
          </Badge>
        )}
      </div>
      <div className={s.mdRowActions}>
        <Button appearance="subtle" size="small" icon={<EditRegular />} aria-label="Edit" onClick={onEdit} />
      </div>
    </div>
  )
}

function SendersManager() {
  const s = useStyles()
  const toast = useToast()
  const [entries, setEntries] = useState<SenderEntry[] | null>(null)
  const [companyNames, setCompanyNames] = useState<ReadonlyMap<string, string>>(new Map())
  const [showRetired, setShowRetired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [term, setTerm] = useState('')
  const [edit, setEdit] = useState<SenderEntry | 'new' | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [senders, domains, companies] = await Promise.all([
        loadIntakeSenders(),
        loadCompanyEmailDomains(),
        loadCompaniesForManagement(),
      ])
      setEntries(mergeSenderEntries(senders, domains))
      setCompanyNames(new Map(companies.map((c) => [c.company_id, c.company_name])))
    } catch (e) {
      setError(errorMessage(e))
      setEntries([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    return (entries ?? [])
      .filter((e) => showRetired || entryIsActive(e))
      .filter((e) => {
        if (!q) return true
        if (e.key.toLowerCase().includes(q)) return true
        const name = routedCompanyName(e, companyNames)
        return !!name && name.toLowerCase().includes(q)
      })
  }, [entries, showRetired, term, companyNames])

  if (entries === null) return <Spinner label="Loading senders…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <Body1 className={s.mdIntro}>
        Two facts live on each entry. <strong>Intake</strong> says who may email documents to the intake
        mailbox — every accepted page is a billed AI read. <strong>Routing</strong> says which company a
        document files to, and it applies to <strong>in-app uploads as well as email</strong>, because both
        record the uploader&rsquo;s own address. A domain&rsquo;s routing beats what the document says.
      </Body1>
      <Body1 className={s.mdIntro}>Only a whole domain can carry routing; a single address cannot.</Body1>
      <Body1 className={s.mdIntro}>
        Senders pinned in configuration are a bootstrap floor and are not listed here, and this list
        governs the SES intake path — the Graph mailbox path has no database and reads the pinned floor
        alone.
      </Body1>
      <div className={s.mdToolbar}>
        <Switch label="Show retired" checked={showRetired} onChange={(_, d) => setShowRetired(d.checked)} />
        <SearchBox
          className={s.searchBox}
          placeholder="Search address, domain or company…"
          value={term}
          onChange={(_, d) => setTerm(d.value)}
        />
        <div className={s.spacer} />
        <Button icon={<AddRegular />} onClick={() => setEdit('new')}>
          Add sender or routing rule
        </Button>
      </div>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>{(entries ?? []).length === 0 ? 'No senders to show.' : 'No senders match.'}</Body1>
      ) : (
        <div className={s.mdList}>
          {visible.map((e) => (
            <SenderRow key={e.key} entry={e} companyNames={companyNames} onEdit={() => setEdit(e)} />
          ))}
        </div>
      )}
      <SenderDialog
        row={edit}
        companyNames={companyNames}
        onClose={() => setEdit(null)}
        onSaved={(message, isError) => {
          setEdit(null)
          toast(message, isError ? 'error' : 'success')
          void load()
        }}
        onError={setError}
      />
    </div>
  )
}

function SenderDialog({
  row,
  companyNames,
  onClose,
  onSaved,
  onError,
}: {
  row: SenderEntry | 'new' | null
  companyNames: ReadonlyMap<string, string>
  onClose: () => void
  /** Full success, OR a partial write (`isError` true) that already changed
      something — either way the caller closes, toasts and reloads, because
      "always reload" is the only way the screen can show what actually
      landed against two masters with no shared transaction. */
  onSaved: (message: string, isError?: boolean) => void
  /** Nothing landed at all — the dialog stays open so the mistake can be fixed. */
  onError: (m: string) => void
}) {
  const s = useStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [key, setKey] = useState('')
  const [intakeOn, setIntakeOn] = useState(false)
  const [company, setCompany] = useState<{ id?: string; name?: string }>({})
  const [saving, setSaving] = useState(false)
  const [confirmOffs, setConfirmOffs] = useState<string[] | null>(null)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setKey('')
      setIntakeOn(false)
      setCompany({})
      return
    }
    setKey(row.key)
    setIntakeOn(row.intake !== null)
    setCompany(
      row.routing
        ? { id: row.routing.company_id, name: routedCompanyName(row, companyNames) ?? row.routing.company_id }
        : {},
    )
    // Seeded from `row` alone — `companyNames` is read once at seed time to
    // resolve the label; a later re-render of the map (e.g. after a reload
    // elsewhere) should not retroactively rewrite what the dialog is showing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row])

  const normalizedKey = isNew ? normalizeSenderKey(key) : existing?.key ?? ''
  const isDomain = normalizedKey.startsWith('@')
  const keyChanged = isNew && key.trim() !== '' && normalizedKey !== key.trim().toLowerCase()
  const ready = isNew ? normalizedKey !== '' && (intakeOn || !!company.id) : true

  // A picked company is routing state, and routing only applies to a domain
  // key. If the user picks a company and then edits the key so it stops being
  // a domain (`jane@x.test` after `@x.test`), the picker unmounts but
  // `company` state survives — leaving `ready` true via a stale `company.id`
  // that `doSave`'s `if (isDomain)` branch never reads, so the primary button
  // would write nothing (no intake row when `intakeOn` is off, no routing
  // write at all) and still report success. Drop the stale state the moment
  // `isDomain` goes false so a re-add starts clean rather than carrying a
  // hidden company id back in if the user retypes the `@`.
  useEffect(() => {
    if (!isDomain) setCompany({})
  }, [isDomain])

  /** What this save would stop, so a turn-off is confirmed rather than silent
      — the same confirmation every other manager's row-level Retire button
      already provides. Empty when nothing is being turned off. */
  function turnOffMessages(): string[] {
    const msgs: string[] = []
    if (existing?.intake && !intakeOn) {
      msgs.push(`Stop accepting emailed documents from ${existing.key}?`)
    }
    if (existing?.routing && !company.id) {
      const name = routedCompanyName(existing, companyNames) ?? existing.routing.company_id
      msgs.push(`Stop filing documents from ${existing.key} to ${name}?`)
    }
    return msgs
  }

  async function doSave() {
    setSaving(true)
    // Intake first — it has no foreign key, so it cannot fail on a bad company.
    try {
      if (intakeOn && !existing?.intake) {
        await createIntakeSender(normalizedKey)
      } else if (!intakeOn && existing?.intake) {
        await updateIntakeSender(existing.intake.id, { is_active: false, row_version: existing.intake.row_version })
      }
    } catch (e) {
      setSaving(false)
      onError(writeError(e, 'Could not save the intake setting'))
      return
    }
    if (isDomain) {
      try {
        if (company.id && !existing?.routing) {
          await createCompanyEmailDomain(normalizedKey, company.id)
        } else if (company.id && existing?.routing && company.id !== existing.routing.company_id) {
          await updateCompanyEmailDomain(existing.routing.id, {
            company_id: company.id,
            row_version: existing.routing.row_version,
          })
        } else if (!company.id && existing?.routing) {
          await updateCompanyEmailDomain(existing.routing.id, {
            is_active: false,
            row_version: existing.routing.row_version,
          })
        }
      } catch (e) {
        // The intake half above already landed — say so rather than reporting
        // a clean failure, and reload so the screen shows what actually happened.
        setSaving(false)
        onSaved(writeError(e, 'The intake setting saved, but the routing did not'), true)
        return
      }
    }
    setSaving(false)
    onSaved(isNew ? 'Sender added' : 'Sender updated')
  }

  function attemptSave() {
    const offs = turnOffMessages()
    if (offs.length > 0) {
      setConfirmOffs(offs)
      return
    }
    void doSave()
  }

  return (
    <>
      <Dialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{isNew ? 'Add sender or routing rule' : 'Edit sender'}</DialogTitle>
            <DialogContent>
              <Field
                label="Email address or domain"
                hint="name@example.com allows one sender to email in; @example.com covers a whole domain and can also carry company routing. Stored lowercase. The string is the identity of both facts, so it cannot be edited later — changing it is retire and re-add."
                className={s.dialogField}
              >
                {isNew ? (
                  <Input
                    value={key}
                    onChange={(_, d) => setKey(d.value)}
                    placeholder="e.g. @seniorlifejohnstown.com or scanners@pas-lp.com"
                  />
                ) : (
                  <Input value={existing?.key ?? ''} readOnly appearance="filled-darker" />
                )}
              </Field>
              {keyChanged && <Body1 className={s.dialogWarn}>Will be stored as &ldquo;{normalizedKey}&rdquo;.</Body1>}
              <div className={s.dialogField}>
                <Switch
                  label="May email documents into intake"
                  checked={intakeOn}
                  onChange={(_, d) => setIntakeOn(d.checked)}
                />
              </div>
              {isDomain ? (
                <Field label="File documents from this domain to" className={s.dialogField}>
                  <div className={s.pickerRow}>
                    <EntityPicker
                      value={company}
                      placeholder="Search companies…"
                      search={(t) =>
                        loadCompaniesForManagement(t).then<EntityOption[]>((cs) =>
                          cs
                            .filter((c) => c.is_active !== false)
                            .map((c) => ({ id: c.company_id, primary: c.company_name, secondary: c.company_id })),
                        )
                      }
                      onSelect={(o) => setCompany({ id: o.id, name: o.primary })}
                    />
                    {company.id && (
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        onClick={() => setCompany({})}
                      >
                        Clear routing
                      </Button>
                    )}
                  </div>
                </Field>
              ) : (
                normalizedKey !== '' && (
                  <Caption1 className={s.mdHint}>
                    Company routing is per domain — a single address cannot carry one.
                  </Caption1>
                )
              )}
              {isDomain && company.id && (
                <Body1 className={s.dialogWarn}>
                  Map a domain only where every document from it belongs to this one company. The
                  organization&rsquo;s own domain, shared-staff sites, vendor domains and the scanner relay
                  are deliberately unmapped so their documents are filed by what the document says.
                </Body1>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button appearance="primary" disabled={!ready || saving} onClick={attemptSave}>
                {isNew ? 'Add sender or routing rule' : 'Save'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <ConfirmDialog
        open={confirmOffs !== null}
        title="Confirm change"
        body={(confirmOffs ?? []).join(' ')}
        confirmLabel="Continue"
        busy={saving}
        onConfirm={() => {
          setConfirmOffs(null)
          void doSave()
        }}
        onCancel={() => setConfirmOffs(null)}
      />
    </>
  )
}

// ---- Vendors (vendor aliases, owner item 10b, 2026-08-13) -------------------
//
// A rule reads back as a full sentence, no source annotation — same reasoning
// as the Accounts tab: staff are the only ones who ever add one here, so
// naming the source ("Manual") tells nobody anything.
//
//   When an invoice arrives from "xerox", attribute the invoice to XEROX
//   BUSINESS SOLUTIONS.
//
// Backed by `vendor_aliases` (extended with a writer, not a new table). A
// match is a DETERMINISTIC OVERRIDE of vendor identity, ahead of vendor
// precedent (`docs/ai.md` §3.2) — stronger than an Accounts-tab default,
// which pass 2 can still weigh against the document. `alias_name` is
// normalized SERVER-SIDE (trimmed, internal whitespace collapsed); the
// preview below mirrors that normalization but the server never trusts it.

/** Preview-only mirror of `normalizeAliasName` in
    `backend/src/http/routes/references.ts` — the server re-normalizes
    regardless of what this sends. */
function normalizeAliasPreview(value: string): string {
  return value.trim().replace(/\s+/g, ' ')
}

function VendorAliasesManager() {
  const s = useStyles()
  const toast = useToast()
  const [all, setAll] = useState<VendorAlias[] | null>(null)
  const [showRetired, setShowRetired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [edit, setEdit] = useState<VendorAlias | 'new' | null>(null)
  const [confirm, setConfirm] = useState<VendorAlias | null>(null)
  const [term, setTerm] = useState('')

  const load = useCallback(async () => {
    setError(null)
    try {
      setAll(await loadVendorAliases())
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  // `vendor_aliases.vendor_name` is a denormalized cache written at create
  // time (`docs/schema.md`), not a live lookup — but it always has a value
  // to fall back to, unlike a coding rule, which is why this tab never
  // needed the id-to-name search `loadVendorNames` used to run here.
  const vendorName = useCallback((a: VendorAlias) => a.vendor_name || a.vendor_id, [])

  /** The rule as a full, true sentence — the whole point of the row. */
  const sentence = useCallback(
    (a: VendorAlias): string =>
      `When an invoice arrives from "${a.alias_name}", attribute the invoice to ${vendorName(a)}.`,
    [vendorName],
  )

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    const haystack = (a: VendorAlias) => [a.alias_name, a.vendor_id, vendorName(a)].join(' ').toLowerCase()
    return (all ?? [])
      .filter((a) => showRetired || a.is_active)
      .filter((a) => !q || haystack(a).includes(q))
  }, [all, showRetired, term, vendorName])

  async function toggleActive(a: VendorAlias) {
    setBusyId(a.id)
    setError(null)
    try {
      await updateVendorAlias(a.id, { is_active: !a.is_active, row_version: a.row_version })
      toast(a.is_active ? 'Rule retired' : 'Rule reactivated')
      setConfirm(null)
      await load()
    } catch (e) {
      setError(writeError(e, 'Could not update rule'))
    } finally {
      setBusyId(null)
    }
  }

  if (all === null) return <Spinner label="Loading vendor rules…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <Body1 className={s.mdIntro}>
        A vendor rule is a deterministic override: it beats vendor precedent and the AI match, ahead
        of everything else. Add one only where the extracted text always means the same vendor.
      </Body1>
      <div className={s.mdToolbar}>
        <Switch label="Show retired" checked={showRetired} onChange={(_, d) => setShowRetired(d.checked)} />
        <SearchBox
          className={s.searchBox}
          placeholder="Search text or vendor…"
          value={term}
          onChange={(_, d) => setTerm(d.value)}
        />
        <div className={s.spacer} />
        <Button icon={<AddRegular />} onClick={() => setEdit('new')}>
          Add rule
        </Button>
      </div>
      {visible.length === 0 ? (
        <Body1 className={s.mdHint}>{(all ?? []).length === 0 ? 'No rules to show.' : 'No rules match.'}</Body1>
      ) : (
        <div className={s.mdList}>
          {visible.map((a) => (
            <MasterRow
              key={a.id}
              title={sentence(a)}
              subtitle={a.evidence ?? undefined}
              wrapTitle
              retired={!a.is_active}
              busy={busyId === a.id}
              onEdit={() => setEdit(a)}
              onToggleActive={() => (a.is_active ? setConfirm(a) : void toggleActive(a))}
            />
          ))}
        </div>
      )}
      <VendorAliasDialog
        row={edit}
        vendorName={vendorName}
        onClose={() => setEdit(null)}
        onSaved={(wasNew) => {
          setEdit(null)
          toast(wasNew ? 'Rule added' : 'Rule updated')
          void load()
        }}
        onError={setError}
      />
      <ConfirmDialog
        open={!!confirm}
        title="Retire rule?"
        body={confirm ? `Retire this rule? ${sentence(confirm)} You can reactivate it later.` : ''}
        confirmLabel="Retire"
        busy={!!busyId}
        onConfirm={() => confirm && void toggleActive(confirm)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  )
}

function VendorAliasDialog({
  row,
  vendorName,
  onClose,
  onSaved,
  onError,
}: {
  row: VendorAlias | 'new' | null
  /** Resolver from `VendorAliasesManager`'s label loader — used only to seed an
      EDIT with a label instead of a raw vendor id; a fresh pick already carries
      its own label via `EntityPicker`'s `onSelect`. */
  vendorName: (a: VendorAlias) => string
  onClose: () => void
  onSaved: (wasNew: boolean) => void
  onError: (m: string) => void
}) {
  const s = useStyles()
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [aliasText, setAliasText] = useState('')
  const [vendor, setVendor] = useState<{ id?: string; name?: string }>({})
  const [evidence, setEvidence] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setAliasText('')
      setVendor({})
      setEvidence('')
      return
    }
    setAliasText(row.alias_name)
    setVendor({ id: row.vendor_id, name: vendorName(row) })
    setEvidence(row.evidence ?? '')
    // `row` is the only thing this effect seeds from — `vendorName` is a
    // memoized callback from the manager and stable across a render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row])

  const normalizedAlias = normalizeAliasPreview(aliasText)
  const aliasChanged = aliasText.trim() !== '' && normalizedAlias !== aliasText.trim()
  const ready = normalizedAlias !== '' && !!vendor.id

  async function save() {
    if (!ready || !vendor.id) return
    setSaving(true)
    try {
      const trimmedEvidence = evidence.trim() === '' ? null : evidence.trim()
      if (isNew) {
        await createVendorAlias({ alias_name: normalizedAlias, vendor_id: vendor.id, evidence: trimmedEvidence })
      } else if (existing) {
        await updateVendorAlias(existing.id, {
          alias_name: normalizedAlias,
          vendor_id: vendor.id,
          evidence: trimmedEvidence,
          row_version: existing.row_version,
        })
      }
      onSaved(isNew)
    } catch (e) {
      onError(writeError(e, 'Could not save rule'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{isNew ? 'Add rule' : 'Edit rule'}</DialogTitle>
          <DialogContent>
            <Field
              label="Text on the invoice"
              hint='Compared trimmed and case-insensitive against the vendor name as printed on the invoice, e.g. "xerox".'
              className={s.dialogField}
            >
              <Input value={aliasText} onChange={(_, d) => setAliasText(d.value)} placeholder="e.g. xerox" />
            </Field>
            {aliasChanged && (
              <Body1 className={s.dialogWarn}>
                Stored as &ldquo;{normalizedAlias}&rdquo; — leading, trailing and repeated spaces are
                collapsed.
              </Body1>
            )}
            <Field label="Attribute to (vendor)" className={s.dialogField}>
              <EntityPicker
                value={vendor}
                placeholder="Search vendors…"
                search={(t) =>
                  searchVendors(t).then<EntityOption[]>((vs) =>
                    vs.map((v) => ({
                      id: v.vendor_id,
                      primary: v.vendor_name || '(unnamed)',
                      secondary: [v.city, v.state_or_province].filter(Boolean).join(', ') || undefined,
                    })),
                  )
                }
                onSelect={(o) => setVendor({ id: o.id, name: o.primary })}
              />
            </Field>
            <Field label="Notes (optional)" className={s.dialogField}>
              <Input
                value={evidence}
                onChange={(_, d) => setEvidence(d.value)}
                placeholder="Why this rule exists"
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
              {isNew ? 'Add rule' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
